/**
 * Axios RESPONSE interceptor -- auth resilience.
 *
 * Extracted from main.js (auth-resilience fix) so its behavior can be unit
 * tested without mounting the whole Vue app -- same precedent as the
 * requireToken.js / requireCapability.js extractions.
 *
 * Behavior on an error response:
 *   - 401 that is an MFA step-up (mfa_required + mfa_challenge in the body,
 *     via the shared `mfaStepUp.isStepUp` predicate): rejected IMMEDIATELY,
 *     untouched, with no retry, no logout, no redirect. This is the ONE
 *     case that skips the retry below -- see the inline comment at the
 *     branch for why a retry is actively unsafe here (fresh single-use
 *     challenge per attempt, plus the retry delay is too slow for the
 *     caller's synchronous catch).
 *   - 401 (and NOT during a storefront token swap): RETRY the same request
 *     ONCE after a short delay. The request-side interceptor in axios.js
 *     re-attaches the current localStorage token, so a plain re-issue picks
 *     up the stored token. If the retry SUCCEEDS (e.g. the API container
 *     finished restarting), we return the successful response and the user
 *     stays logged in. If the retry ALSO 401s (genuine token
 *     revocation/expiry), we dispatch `logout` and redirect to /login.
 *   - The retried request is flagged (`config.__retriedOn401`) so it never
 *     retries a second time -- a 401 on the retry goes straight to logout,
 *     preventing an infinite retry loop.
 *   - `window._swappingToken`: preserved suppression -- during a storefront
 *     token swap we neither retry nor logout (a concurrent 401 is expected
 *     while the new token is being installed).
 *   - 404: silent (no toast, no logout) -- unchanged.
 *   - 403 `store_gate_required` (card nrNUCjgU): the session has not passed
 *     the store's password on the SERVER. The visitor is sent back to that
 *     store's password wall and the browser's cleared marker is dropped. No
 *     toast, no logout, no retry.
 *   - Other 4xx/5xx: `raiseError` toast, no logout, no retry -- unchanged.
 *   - Cancellation (`CanceledError` / `AbortError` / `ERR_CANCELED`, which
 *     also has no `error.response`): rejected SILENTLY -- no toast, no
 *     logout. A superseded in-flight request is an expected outcome, not a
 *     failure; see `isCancellation`.
 *   - Network error (no `error.response`, and not a cancellation):
 *     `raiseError`, no logout -- unchanged.
 *   - ANONYMOUS storefront session (Jake, 2026-10-01): when the single retry
 *     also 401s and the session is the per-visitor anonymous cart session on
 *     a `/shop/<slug>` page, the visitor is NEVER sent to /login -- they have
 *     no credentials to sign in with. The session is silently re-minted
 *     (`recoverAnonymousSession`) and the request replayed once on the new
 *     session; if that is not possible they land on the store homepage
 *     (`navigateToStoreHome`). A CREDENTIALED session on a storefront page
 *     goes to that store's home the same way, never /login (Jake,
 *     2026-10-02); off the storefront it keeps the /login path.
 *   - A 401 on a request SENT with a bearer that is no longer the current one
 *     describes a session that has already been replaced (a storefront swap
 *     or re-mint). It is rejected without retry or logout -- acting on it
 *     would end the replacement. Same for a retry that 401s while a token
 *     swap is in flight.
 *   - `skipAuthRecovery: true` (SKIP_AUTH_RECOVERY) on a request: its 401 is
 *     rejected untouched -- no retry, no logout, no redirect. For callers
 *     whose 401 is information rather than a verdict on the session: the
 *     boot probes, and the storefront gate's held-session check (which
 *     re-mints on it itself).
 *   - Per-request opt-out: a request whose config carries
 *     `suppressErrorToast: true` (SUPPRESS_ERROR_TOAST) gets NO `raiseError`
 *     toast for a network error or a non-401 status. It is still rejected,
 *     and 401 handling (retry, logout) is unchanged. For callers that render
 *     their OWN error state for the request, so the user sees one error
 *     rather than a toast on top of it (the cart's cart_prices error with
 *     Retry, #3884).
 *
 * Session-dead latch (loop fix): once a 401 has survived the single retry and
 * `logout` + redirect have fired, the session is genuinely dead. A module-level
 * `sessionEnded` latch is set at that moment. WHILE it is set, every subsequent
 * 401 is rejected INERTLY -- no retry, no second `logout`, no second redirect --
 * so the flood of post-logout 401s (the App.vue-remount `impersonate/status`
 * probe, any background poll) can no longer drive the interceptor. This closes
 * the infinite logout loop: without the latch, `router.push('/login')` remounts
 * App.vue -> `mounted()` calls `impersonationStore.hydrate()` (a token still in
 * localStorage) -> `GET /impersonate/status` 401s -> retry -> 401 ->
 * logout+redirect -> remount -> ... every ~750ms until Chrome freezes.
 *
 * The latch clears on the FIRST successful (2xx) response (the fulfilled branch)
 * -- a successful authenticated request proves the session is alive again and
 * self-heals the latch so it can never wedge a genuinely-logged-in user -- and
 * also via `resetAuthInterceptorState()`, which the login-success path
 * (`stores/account.js` hydrate) calls so a fresh login re-arms the machinery.
 *
 * Why this lived as an immediate logout before: any 401 from any endpoint
 * dispatched `logout` with no retry. During an API container restart a
 * stray/in-flight request gets a transient 401 (or the proxy briefly returns
 * one) and the old interceptor immediately wiped the session even though the
 * localStorage token survived the restart and was still valid.
 */

// Shared "is this 401 an MFA step-up?" predicate -- see mfaStepUp.isStepUp
// in loginMfa.js. Importing it (rather than re-deriving the shape check
// here) keeps ONE definition of "this is a step-up" so this interceptor
// and TheLoginPage.vue can never drift out of agreement about what counts.
import { mfaStepUp } from '@/components/loginMfa'
import { loginRedirectFor } from '@/router/postLoginRedirect'
import { isSupersededSessionError, sentBearer } from '@/router/sessionBearer'
import { isAnonymousStorefrontSessionNow } from '@/router/sessionProvenance'
import { currentStorefrontSlug } from '@/router/storefrontSlug'
import { clearStorefrontNavCache } from '@/composables/useStorefrontNav'
import { PASSWORD_GATE_ROUTE, clearStoreGate } from '@/router/storefrontPasswordGate'

// The login route path. `router/index.js` registers it as
// `{ path: '/login', name: 'login_page' }`. We push by path so this module
// has no dependency on the route-name string.
export const LOGIN_ROUTE = '/login'

// Delay before the single 401 retry. Long enough for a container recreate /
// proxy blip to settle, short enough not to be user-visible as a hang.
export const RETRY_DELAY_MS = 750

// Random jitter (0..RETRY_JITTER_MS) added to RETRY_DELAY_MS per retry. During
// a real backend outage every in-flight request 401s at once; without jitter
// all their retries fire in the same ~750ms instant -- a synchronized pulse
// that doubles inbound load exactly when the API is trying to recover. Jitter
// desynchronizes the concurrent retries (thundering-herd mitigation).
export const RETRY_JITTER_MS = 250

// Delay before the single retry, with jitter. Uses Math.random for spread;
// no cryptographic requirement (this is load-desync, not a security nonce).
function retryDelayMs() {
  return RETRY_DELAY_MS + Math.floor(Math.random() * RETRY_JITTER_MS)
}

// Marks a request that has already been retried once, so the retry itself
// (if it 401s) does not retry again.
const RETRY_FLAG = '__retriedOn401'

// Session-dead latch. Set true the moment an interceptor-driven logout fires
// (a 401 that survived the single retry). While set, subsequent 401s are inert
// -- see the module docblock. Cleared on the first 2xx (self-heal) or via
// resetAuthInterceptorState() from the login-success path. Module-level because
// the interceptor is installed once (main.js) on the singleton axios instance.
let sessionEnded = false

/**
 * Clear the session-dead latch so the retry/redirect machinery is re-armed.
 * Call from the login-success path (stores/account.js hydrate) so a real
 * re-login re-arms the interceptor even before any request has succeeded.
 * Idempotent and safe to call when the latch is already clear.
 */
/** Request-config key: skip the global error toast for this request. */
export const SUPPRESS_ERROR_TOAST = 'suppressErrorToast'

/** Request-config key: a 401 on this request is the caller's to handle. */
export const SKIP_AUTH_RECOVERY = 'skipAuthRecovery'

/**
 * Re-address a request from the dead anonymous account to its replacement.
 * Storefront calls name the visitor's own account in the path
 * (`/accounts/<id>/update_cart_quantity`); the new bearer cannot touch the
 * old row, so a replay to the old path would 404.
 */
function readdress(url, oldAccountId, newAccountId) {
  if (typeof url !== 'string' || oldAccountId == null || newAccountId == null) {
    return url
  }
  const escaped = String(oldAccountId).replace(/[.*+?^${}()|[\]\\]/g, '\\$&')
  const pattern = new RegExp(`(^|/)accounts/${escaped}(?=/|$|\\?)`)
  return url.replace(pattern, `$1accounts/${String(newAccountId)}`)
}

// Requests that may be replayed ONCE on a freshly minted anonymous session.
//
// An allowlist, not a denylist: the replay is a FIRST execution on a new
// account, so it is safe only where re-running is idempotent and the request
// names nothing of the dead account but its own id in the path. Reads (GET /
// HEAD), and the cart writes, which set absolute state on the visitor's own
// cart. Never money or orders: checkout, pending orders, /stripe/ and /order/
// are refused even as reads -- their caller must see the failure and decide
// (gate finding P2-1 on #3957).
const REPLAY_DENIED_PATH = /(^|\/)(stripe|order)\/|\/(checkout|create_pending_order)(\/|$|\?)/
const REPLAY_CART_WRITE = /(^|\/)accounts\/[^/]+\/(update_cart_quantity|delete_cart|cart_prices)(\/|$|\?)/

function isReplayable(config) {
  const url = typeof config.url === 'string' ? config.url : ''
  if (REPLAY_DENIED_PATH.test(url)) return false
  const method = String(config.method || 'get').toLowerCase()
  if (method === 'get' || method === 'head') return true
  return REPLAY_CART_WRITE.test(url)
}

function defaultNavigateToStoreHome(router) {
  return (slug) => {
    const path = `/shop/${encodeURIComponent(slug)}`
    const href = router && typeof router.resolve === 'function'
      ? router.resolve(path).href
      : path
    // A FULL load, not router.push: the session gate is what mints, and it
    // runs on mount -- an in-app push within the same store would reuse the
    // mounted gate and never establish a session.
    window.location.assign(href)
  }
}

// The API's "this session has not passed the store's password" refusal
// (api/src/services/store_gate_pass.py, STORE_GATE_REQUIRED_CODE). Read from
// either error shape: the Flask handler's flat `{code}` or SAFRS's JSON:API
// `{errors: [{code}]}`.
export const STORE_GATE_REQUIRED_CODE = 'store_gate_required'

function isStoreGateRequired(error) {
  if (error?.response?.status !== 403) return false
  const data = error.response.data || {}
  if (data.code === STORE_GATE_REQUIRED_CODE) return true
  const first = Array.isArray(data.errors) ? data.errors[0] : null
  return first?.code === STORE_GATE_REQUIRED_CODE
}

// The order build's Net Checkout refusals (api/src/services/
// net_checkout_credential.py). The storefront answers each IN PLACE on the
// checkout dialog (reopen the PIN field, re-ask the terms), so none is the
// password wall, the "store configuration changed" dialog, or a toast.
export const NET_PIN_REQUIRED_CODE = 'net_pin_required'
export const NET_TERMS_REQUIRED_CODE = 'net_terms_required'
export const NET_TERMS_STALE_CODE = 'net_terms_stale'
const NET_CHECKOUT_REFUSAL_CODES = [
  NET_PIN_REQUIRED_CODE,
  NET_TERMS_REQUIRED_CODE,
  NET_TERMS_STALE_CODE,
]

/**
 * The Net Checkout refusal code on a failed request, or null. Read from
 * either error shape (the Flask handler's flat `{code}` or SAFRS's
 * `{errors: [{code}]}`), on the statuses the API uses for them (403 / 400 /
 * 409). Branch on the CODE, never the status alone: a 409 is also the
 * staleness guard and a 403 is also the store password wall.
 */
export function netCheckoutRefusalCode(error) {
  const status = error?.response?.status
  if (status !== 403 && status !== 400 && status !== 409) return null
  const data = error.response.data || {}
  const first = Array.isArray(data.errors) ? data.errors[0] : null
  const code = data.code || first?.code
  return NET_CHECKOUT_REFUSAL_CODES.includes(code) ? code : null
}

function toastSuppressed(error) {
  return Boolean(error && error.config && error.config[SUPPRESS_ERROR_TOAST])
}

export function resetAuthInterceptorState() {
  sessionEnded = false
}

function delay(ms) {
  return new Promise((resolve) => setTimeout(resolve, ms))
}

/**
 * Is this rejection a CANCELLATION rather than a failure?
 *
 * A cancelled request is the EXPECTED outcome of superseding an in-flight
 * request, not an error: the caller aborted it deliberately and a newer
 * request already owns the result. It must never produce a user-facing toast.
 *
 * This matters here because a cancellation carries no `error.response`, so it
 * is otherwise indistinguishable from a genuine transport failure and takes
 * the network-error branch below. That is the bug this predicate closes:
 * changing the SKU in the product editor aborts the prior
 * `GET /api/supplier/<key>/product/<sku>` (useSanmarProductMeta cancels on
 * each change), and the abort raised a red "An error occurred when making
 * your request" toast on a path that had just succeeded -- the superseding
 * request returned 200 and the product autosaved. The console stayed clean,
 * because nothing had actually gone wrong.
 *
 * Three signatures, because the cancellation can originate in either layer:
 *   - axios >= 1.x rejects with a `CanceledError` carrying `code`
 *     `ERR_CANCELED`. Either field alone is accepted: the name survives
 *     minification of the class but not a structured-clone round trip, and
 *     the code survives both -- requiring BOTH would make the guard
 *     fragile in exactly the cases it exists for.
 *   - a raw `AbortController` rejection surfaces as a DOMException named
 *     `AbortError` (what `fetch` and pre-1.x axios signal-aborts produce).
 *
 * Deliberately narrow: ONLY cancellations are exempted. Every other
 * response-less rejection (`ERR_NETWORK`, a timeout, an offline browser) is
 * still a real failure the user needs to see, and still toasts.
 *
 * Mirrors `isRequestCancelled` in `@/utils/sku-search` and the inline check in
 * `useSanmarProductMeta`. Those guard their own callers' local state; this
 * one guards the global toast, and runs FIRST -- which is why their correct
 * guards did not prevent the toast.
 */
function isCancellation(error) {
  if (!error) return false
  return (
    error.name === 'CanceledError' ||
    error.name === 'AbortError' ||
    error.code === 'ERR_CANCELED'
  )
}

/**
 * Install the response interceptor on the given axios instance.
 *
 * @param {object} deps
 * @param {object} deps.axiosInstance The axios instance. Must expose
 *   `interceptors.response.use` AND be callable as `axiosInstance(config)`
 *   to re-issue a request (the retry).
 * @param {object} deps.store  Vuex store (dispatch: raiseError, logout).
 * @param {object} deps.router Vue Router instance (currentRoute.value.path,
 *   push). Optional -- when absent, logout still fires but no redirect.
 * @param {function} [deps.recoverAnonymousSession] `({ slug }) =>
 *   Promise<{ oldAccountId, newAccountId }>` -- mint a replacement anonymous
 *   storefront session. Absent: an anonymous visitor goes to the store
 *   homepage instead.
 * @param {function} [deps.navigateToStoreHome] `(slug) => void`. Defaults to a
 *   full page load of `/shop/<slug>`.
 */
export function installAuthResponseInterceptor({
  axiosInstance,
  store,
  router,
  recoverAnonymousSession = null,
  navigateToStoreHome = null,
}) {
  const goToStoreHome = navigateToStoreHome || defaultNavigateToStoreHome(router)

  function redirectToLogin() {
    // A shopper on a storefront page goes back to THEIR STORE's home, never
    // the generic /login (Jake, 2026-10-02 -- there is no storefront
    // credential sign-in for the event). A FULL load, like the anonymous
    // fallback below: the session gate establishes a session only on mount.
    // Read from the address bar, like every storefront decision here (see
    // currentStorefrontSlug).
    const slug = currentStorefrontSlug()
    if (slug) {
      goToStoreHome(slug)
      return
    }
    if (!router) return
    const currentPath = router.currentRoute?.value?.path
    // Guard against a redirect loop when the logout happens while the user
    // is already on the login page.
    if (currentPath === LOGIN_ROUTE) return
    // push returns a promise; swallow a NavigationDuplicated-style rejection.
    // Mid-session expiry: keep the page they were on (path + query + hash) so
    // signing back in returns there. loginRedirectFor yields the plain
    // /login for the neutral entry paths and anything it refuses.
    const here = router.currentRoute?.value
    Promise.resolve(
      router.push(loginRedirectFor(here?.fullPath ?? here?.path))
    ).catch(() => {})
  }

  /**
   * Back to the password wall of the store the visitor is on (card nrNUCjgU).
   *
   * The server enforces the store password now, so its 403 outranks the
   * browser's "cleared" marker: drop the marker, then show the wall with a
   * `redirect` back to where they were. Not an auth failure -- no logout, no
   * toast, no re-mint (a new session would be refused the same way).
   */
  function routeToStoreGate() {
    const slug = currentStorefrontSlug()
    if (!slug || !router) return
    clearStoreGate(slug)
    const current = router.currentRoute?.value || {}
    if (current.name === PASSWORD_GATE_ROUTE) return
    const redirect = current.fullPath || window.location.pathname
    Promise.resolve(
      router.replace({
        name: PASSWORD_GATE_ROUTE,
        params: { slug },
        query: { redirect },
      })
    ).catch(() => {})
  }

  function logoutAndRedirect(error) {
    // Latch the session as dead BEFORE dispatching, so any 401 that lands
    // synchronously in the wake of logout+redirect (the App.vue-remount
    // impersonate/status probe) sees the latch and stays inert. This is the
    // single site that arms the latch: first genuine 401 -> logout + redirect
    // + latch; all subsequent 401s inert until a success clears it.
    sessionEnded = true
    store.dispatch('raiseError', error)
    store.dispatch('logout')
    redirectToLogin()
  }

  /**
   * The anonymous visitor's dead session: replace it and replay the request
   * once, or -- if a replacement cannot be had -- send them to the store
   * homepage, whose gate mints a fresh one. Never /login (Jake, 2026-10-01).
   *
   * The CART on the dead session is not carried over: its bearer is gone
   * and nothing server-side lets a new session claim the old row. The
   * replayed request keeps what the shopper just asked for (a cart write is
   * an ABSOLUTE quantity, so replaying it once is idempotent).
   */
  async function recoverAnonymousStorefront(config, slug, originalError) {
    if (typeof recoverAnonymousSession === 'function') {
      let recovered = null
      try {
        recovered = await recoverAnonymousSession({ slug })
      } catch {
        recovered = null
      }
      if (recovered) {
        // The visitor has a working session again either way. A request
        // outside the replay allowlist is NOT re-run: its caller gets the
        // original failure, with no logout and no redirect.
        if (!isReplayable(config)) return Promise.reject(originalError)
        const replay = {
          ...config,
          [RETRY_FLAG]: true,
          url: readdress(config.url, recovered.oldAccountId, recovered.newAccountId),
        }
        // The replay runs on the NEW session; whatever it returns is the
        // caller's to handle. It is flagged, so a 401 on it is rejected
        // without another retry or a logout.
        return axiosInstance(replay)
      }
    }
    // Inert until the page reloads: concurrent 401s from the dead session
    // must not race this navigation with a /login redirect.
    sessionEnded = true
    store.dispatch('logout')
    goToStoreHome(slug)
    return Promise.reject(originalError)
  }

  axiosInstance.interceptors.response.use(
    (response) => {
      // A successful authenticated response means the session is alive again.
      // Clear the session-dead latch (self-heal) so the latch can never wedge
      // a genuinely-logged-in user, and so a fresh session re-arms the
      // retry/redirect machinery.
      if (sessionEnded) sessionEnded = false
      return response
    },
    async function onResponseError(error) {
      if (!error || !error.response) {
        // A CANCELLATION is not a failure -- reject it silently so the caller
        // can settle its own state, with no toast and no logout. See
        // isCancellation() for why this must be checked here rather than left
        // to the callers' own abort guards (this interceptor runs first).
        if (isCancellation(error)) return Promise.reject(error)

        // Network error / no response -- do NOT log out (a backend blip or
        // offline should not wipe a valid session).
        if (store && !toastSuppressed(error)) store.dispatch('raiseError', error)
        return Promise.reject(error)
      }

      const status = error.response.status

      if (status === 401) {
        // MFA step-up exemption: a correct password on an MFA-enabled
        // account arrives here as a 401 carrying {mfa_required,
        // mfa_challenge} -- the server withholding the token pending a
        // second factor, NOT a dead session. Two things make this 401
        // uniquely unsafe to retry (unlike an ordinary 401, where a retry
        // is harmless): (1) each login attempt mints a FRESH, single-use
        // challenge server-side, so a retried /auth/login would return a
        // DIFFERENT challenge than the one the caller is about to read off
        // this response, silently invalidating it out from under the UI;
        // (2) the retry's ~750ms delay means even a same-challenge replay
        // would arrive too late for TheLoginPage.vue's synchronous catch,
        // which is what leaves the user stuck on the login form with the
        // code-entry prompt never appearing. Reject with the ORIGINAL
        // error, untouched, on the first response -- no retry, no logout,
        // no redirect -- so the caller's catch (TheLoginPage.vue) can read
        // the challenge and switch to the code-entry step.
        if (mfaStepUp.isStepUp(error)) {
          return Promise.reject(error)
        }

        // The caller handles this 401 itself (boot probes; the storefront
        // gate's held-session check, which re-mints on it).
        if (error.config && error.config[SKIP_AUTH_RECOVERY]) {
          return Promise.reject(error)
        }

        // Session-dead latch: if a genuine logout has already fired, the
        // session is dead. Do NOT retry, do NOT log out again, do NOT redirect
        // again -- just reject so the caller's own catch (e.g. hydrate's)
        // handles it. This is what breaks the infinite logout loop: the flood
        // of post-logout 401s (impersonate/status, any poll) become inert.
        if (sessionEnded) {
          return Promise.reject(error)
        }

        // During a storefront token swap, a concurrent 401 is expected while
        // the new token is being installed. Suppress both retry and logout
        // (preserve the #448 swap-guard behavior exactly).
        if (window._swappingToken) {
          return Promise.reject(error)
        }

        // Sent with a bearer that is no longer the current one: this 401 is
        // about a session that has already been replaced. Retrying it or
        // logging out would end the REPLACEMENT (diag mechanism B: a new
        // token deleted ~20ms after it was written).
        if (isSupersededSessionError(error)) {
          return Promise.reject(error)
        }

        const config = error.config || {}

        // Loop guard: if this request was ALREADY the retry, do NOT retry
        // again and do NOT log out here. Just reject so the rejection
        // propagates back to the first-attempt handler's catch below, which
        // owns the single logout decision. (A request only ever arrives here
        // pre-flagged because THIS interceptor issued it as a retry, so there
        // is always a first-attempt catch waiting for it. Keeping logout in
        // exactly one place avoids a double logout/redirect when the retried
        // request re-enters this interceptor.)
        if (config[RETRY_FLAG]) {
          return Promise.reject(error)
        }

        // First 401 on this request: retry it ONCE. The request-side
        // interceptor (axios.js) re-attaches the current localStorage token,
        // so a plain re-issue picks up the stored token -- if the API came
        // back after a restart, this succeeds and the user stays logged in.
        const retryConfig = { ...config, [RETRY_FLAG]: true }
        await delay(retryDelayMs())
        try {
          return await axiosInstance(retryConfig)
        } catch (retryError) {
          // The retry also failed. If it 401'd, the token is genuinely dead
          // -> logout + redirect (the single logout site). For a non-401
          // retry failure, surface it without logging out.
          if (retryError?.response?.status === 401) {
            // Re-check what the first attempt checked: the swap may have
            // STARTED during the retry delay, or the retry may have gone out
            // on a bearer that has since been replaced. Either way this 401
            // is not about the current session (diag mechanism D).
            if (window._swappingToken || isSupersededSessionError(retryError)) {
              return Promise.reject(retryError)
            }
            const slug = currentStorefrontSlug()
            // Judged against the bearer the failed request actually carried,
            // stated explicitly rather than relying on the superseded check
            // above having proved it equals the current one.
            if (slug && isAnonymousStorefrontSessionNow(sentBearer(retryError.config))) {
              return recoverAnonymousStorefront(config, slug, retryError)
            }
            logoutAndRedirect(retryError)
          } else if (
            store &&
            retryError?.response?.status !== 404 &&
            !toastSuppressed(retryError)
          ) {
            store.dispatch('raiseError', retryError)
          }
          return Promise.reject(retryError)
        }
      }

      if (netCheckoutRefusalCode(error)) {
        // Answered in place by the checkout dialog (see above): no wall, no
        // toast, no logout. NOTE: this suppresses the toast for EVERY caller,
        // so any future non-checkout caller that can receive net_pin_required
        // or net_terms_* must handle those codes itself.
        return Promise.reject(error)
      }

      if (isStoreGateRequired(error)) {
        // The pass is gone: the cached category names were served under it,
        // so drop them and hide any nav already on screen.
        clearStorefrontNavCache()
        routeToStoreGate()
        return Promise.reject(error)
      }

      if (status !== 404 && !toastSuppressed(error)) {
        // 4xx/5xx other than 401/404 -> toast, no logout, no retry (unless
        // the request opted out: its caller shows the error itself).
        store.dispatch('raiseError', error)
      }
      // 404 stays silent.

      return Promise.reject(error)
    }
  )
}
